import { type Dispatch, type ReactElement, type SetStateAction, useCallback, useMemo, useState } from 'react'
import {
  isDefined,
  isEmpty,
  isNotDeleted,
  isNotEmpty,
  isWorkflowStageId,
  pluralize,
  searchIncludes,
  type WorkflowStage,
  type WorkflowStageId,
} from '@terros/common'
import { CloseButton, Combobox, Input, type InputBaseProps, Pill, PillsInput, useCombobox } from '@mantine/core'
import { useTranslation } from '@front/model'
import { type MultiSelectFilterBehavior, useMultiSelectFilterState } from '../pickers'
import { AddStageOption, getFilteredSelectOptions } from './StageSelect'

type StageMultiSelectProps = {
  value: WorkflowStageId[]
  setValue: Dispatch<SetStateAction<WorkflowStageId[]>>
  stages: WorkflowStage[]
  behavior: MultiSelectFilterBehavior
  allOptionLabel?: string
  inputProps?: InputBaseProps
  allowArchived?: boolean
  placeholder?: string
  hideInitial?: boolean
  onAddNewStage?: (name: string) => void
  compact?: boolean
}

export const WorkflowStageMultiSelect = ({
  value,
  setValue,
  stages,
  behavior,
  allOptionLabel,
  inputProps,
  allowArchived,
  placeholder,
  hideInitial,
  onAddNewStage,
  compact = false,
}: StageMultiSelectProps): ReactElement => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const combobox = useCombobox({
    onDropdownOpen: () => {
      combobox.focusSearchInput()
      setSearch('')
    },
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  const visibleStages = useMemo(
    () => (hideInitial ? stages.filter((stage) => !stage.isInitial) : stages),
    [hideInitial, stages]
  )
  const selectableStageIds = useMemo(
    () => visibleStages.filter((stage) => allowArchived || isNotDeleted(stage)).map((stage) => stage.id),
    [allowArchived, visibleStages]
  )

  const { isAllSelected, selectedCount, isSelected, toggle, selectRow, selectAll, getRowAction, doesToggleSelectAll } =
    useMultiSelectFilterState({
      allValues: selectableStageIds,
      selectedValues: value,
      setSelectedValues: setValue,
      behavior,
    })

  const stagesMap = useMemo(
    () => new Map<WorkflowStageId, WorkflowStage>(stages.map((stage) => [stage.id, stage])),
    [stages]
  )

  const filtered = useMemo(
    () => visibleStages.filter((stage) => searchIncludes([JSON.stringify(stage.data)], search)),
    [search, visibleStages]
  )

  const comboboxOptions = useMemo(() => {
    if (isEmpty(filtered)) return <Combobox.Empty>{t('No stages found')}</Combobox.Empty>
    return getFilteredSelectOptions({
      filtered,
      allowArchived,
      isItemSelected: isSelected,
      onItemToggle: toggle,
      getItemRowAction: getRowAction,
      doesItemToggleSelectAll: doesToggleSelectAll,
    })
  }, [allowArchived, doesToggleSelectAll, filtered, getRowAction, isSelected, t, toggle])

  const handleOptionSelect = useCallback(
    (selectedValue: string): void => {
      if (selectedValue === 'add') {
        if (isDefined(onAddNewStage) && isNotEmpty(search)) {
          onAddNewStage(search)
          setSearch('')
        }
        return
      }
      if (!isWorkflowStageId(selectedValue)) return
      selectRow(selectedValue)
    },
    [onAddNewStage, search, selectRow]
  )

  const emptyDisplayValue = useMemo(() => {
    if (isNotEmpty(placeholder)) return placeholder
    if (behavior.emptyBehavior === 'all') return allOptionLabel ?? t('All Stages')
    return t('No stage selected')
  }, [allOptionLabel, behavior.emptyBehavior, placeholder, t])

  const displayValue = useMemo(() => {
    if (isEmpty(value)) return <Input.Placeholder>{emptyDisplayValue}</Input.Placeholder>
    if (isAllSelected) return <Input.Placeholder>{allOptionLabel ?? t('All Stages')}</Input.Placeholder>
    if (compact) return `${selectedCount} ${pluralize(selectedCount, t('stage'), t('stages'))}`
    return (
      <Pill.Group>
        {value.map((item) => (
          <Pill key={item} withRemoveButton onRemove={() => toggle(item)}>
            {stagesMap.get(item)?.name ?? item}
          </Pill>
        ))}
      </Pill.Group>
    )
  }, [allOptionLabel, compact, emptyDisplayValue, isAllSelected, selectedCount, stagesMap, t, toggle, value])

  const handleToggleDropdown = (): void => {
    if (inputProps?.disabled) return
    combobox.toggleDropdown()
  }

  const rightSection = useMemo(() => {
    if (isEmpty(value) || isAllSelected || !compact) return <Combobox.Chevron />
    return (
      <CloseButton
        size='sm'
        aria-label={t('Check all stages')}
        onClick={(event) => {
          event.stopPropagation()
          selectAll()
        }}
      />
    )
  }, [compact, isAllSelected, selectAll, t, value])

  return (
    <Combobox store={combobox} onOptionSubmit={handleOptionSelect} position='bottom-start' width={360}>
      <Combobox.Target>
        <PillsInput
          flex={!compact ? 1 : undefined}
          {...inputProps}
          pointer
          onClick={handleToggleDropdown}
          rightSection={rightSection}
          rightSectionPointerEvents='all'
        >
          {displayValue}
        </PillsInput>
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder={t('Search stages')}
        />
        <Combobox.Options mah={400} style={{ overflowY: 'auto' }}>
          {comboboxOptions}
          {isNotEmpty(search) && isDefined(onAddNewStage) && <AddStageOption search={search} />}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
